import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { isStudentActiveInSchoolYear } from '../utils/isStudentActiveInSchoolYear';
import { createSchoolYearChangeHandler } from '../utils/usePhdSchoolYearOptions';
import { resolveStudentLabel } from '../usePhdReferenceData';

const PhdIndividualPlansEditForm = ({
  studentDisplayNameMap,
  students,
  schoolYears,
  currentUserPersonId,
  isAdmin = false,
  isEdit = false,
  currentPlanId,
  schoolYearOptions,
  ensureSchoolYearSelection,
  isCreatingSchoolYear,
  isSubmitting,
  isStudentContext = false,
  beforeSubmitContent = null,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const selectedStudentId = AntdForm.useWatch('phd_student_id');
  const handleSchoolYearChange = createSchoolYearChangeHandler(
    form,
    ensureSchoolYearSelection,
  );

  const hasAvailableThesisYear = (student) =>
    (student.theses || []).some((thesis) => {
      const schoolYearId = Number(thesis.phd_school_year_id);
      const hasAnotherPlan = (student.individual_plans || []).some(
        (plan) =>
          Number(plan.phd_school_year_id) === schoolYearId &&
          Number(plan.id) !== Number(currentPlanId),
      );

      return (
        !hasAnotherPlan &&
        isStudentActiveInSchoolYear(student, schoolYears, schoolYearId)
      );
    });

  const filteredStudentOptions = useMemo(() => {
    if (isStudentContext) {
      return [];
    }

    const eligibleStudents = (students || []).filter((student) => {
      if (!hasAvailableThesisYear(student)) {
        return false;
      }

      if (isAdmin) {
        return true;
      }

      const assignedTheses = (student.theses || []).filter(
        (thesis) => Number(thesis.phd_student_id) === Number(student.id),
      );

      if (assignedTheses.length === 0) {
        return false;
      }

      if (!currentUserPersonId) {
        return false;
      }

      return assignedTheses.some(
        (thesis) =>
          Number(thesis.phd_supervisor_id) === Number(currentUserPersonId),
      );
    });

    if (
      selectedStudentId &&
      !eligibleStudents.some((student) => student.id === selectedStudentId)
    ) {
      const selectedStudent = (students || []).find(
        (student) => student.id === selectedStudentId,
      );
      if (selectedStudent) {
        eligibleStudents.push(selectedStudent);
      }
    }

    return eligibleStudents.map((student) => ({
      value: student.id,
      label: resolveStudentLabel(student, studentDisplayNameMap),
    }));
  }, [
    currentPlanId,
    currentUserPersonId,
    isAdmin,
    isEdit,
    isStudentContext,
    selectedStudentId,
    studentDisplayNameMap,
    students,
    schoolYears,
  ]);

  useEffect(() => {
    if (
      !selectedStudentId ||
      isStudentContext ||
      isEdit ||
      filteredStudentOptions.some(
        (option) => option.value === selectedStudentId,
      )
    ) {
      return;
    }

    form.setFieldValue('phd_student_id', undefined);
  }, [
    filteredStudentOptions,
    form,
    isEdit,
    isStudentContext,
    selectedStudentId,
  ]);

  const sections = useMemo(
    () => [
      {
        key: 'basic',
        title: t('Základné informácie'),
        fields: [
          !isStudentContext && {
            name: 'phd_student_id',
            label: t('Študent'),
            type: 'select',
            options: filteredStudentOptions,
            componentProps: {
              onChange: () =>
                form.setFieldValue('phd_school_year_id', undefined),
            },
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'phd_school_year_id',
            label: (
              <span>
                {t('Školský rok')}
                <PhdHelpHint
                  label={t('Čo znamená pôvodný školský rok plánu?')}
                  title={t(
                    'Ide o rok vzniku plánu. Jeden canonical plán platí počas celého štúdia; nevytvára sa jeho ročná kópia.',
                  )}
                />
              </span>
            ),
            type: 'select',
            options: schoolYearOptions,
            componentProps: {
              onChange: handleSchoolYearChange,
              loading: isCreatingSchoolYear,
              disabled: !selectedStudentId,
            },
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'date_dissertation_application',
            label: t('Dátum prihlášky'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 12,
            rules: [required(t)],
          },
          {
            name: 'date_dissertation_submission',
            label: t('Dátum odovzdania'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 12,
            rules: [required(t)],
          },
          {
            name: 'date_study_start',
            label: t('Začiatok štúdia'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 12,
            rules: [required(t)],
          },
          {
            name: 'date_study_end',
            label: t('Koniec štúdia'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 12,
            rules: [required(t)],
          },
          {
            name: 'thesis_title',
            label: t('Názov témy'),
            type: 'text',
            rules: [required(t)],
            colSpan: 24,
          },
        ],
      },
      {
        key: 'thesis',
        title: t('Tézy a sylaby'),
        fields: [
          {
            name: 'thematic_areas',
            label: t('Tematické oblasti'),
            type: 'editor',
            colSpan: 24,
          },
          {
            name: 'thesis_required_literature',
            label: t('Povinná literatúra'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
          },
          {
            name: 'thesis_recommended_literature',
            label: t('Odporúčaná literatúra'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
          },
          {
            name: 'thesis_recommended_lectures',
            label: t('Odporúčané prednášky'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
          },
        ],
      },
    ],
    [
      t,
      filteredStudentOptions,
      schoolYearOptions,
      isStudentContext,
      handleSchoolYearChange,
      isCreatingSchoolYear,
      form,
      selectedStudentId,
    ],
  );

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-individual-plans"
      isSubmitting={isSubmitting}
      beforeSubmitContent={beforeSubmitContent}
      hideSubmit={isReadOnly}
    />
  );
};

export default PhdIndividualPlansEditForm;
